import type { TransactionEvent } from "@sentry/core";

const TRACE_STATUS_OK = "ok";
const HEALTHY_TRANSACTION_SAMPLE_RATE = 0.05;
const MAX_UINT_32 = 0xffffffff;

function hasErrorStatus(status: string | undefined): boolean {
  return Boolean(status && status !== TRACE_STATUS_OK);
}

/**
 * Deterministically samples healthy transactions using a hash of the trace ID.
 * The same trace ID always produces the same keep/drop decision, and roughly
 * HEALTHY_TRANSACTION_SAMPLE_RATE of healthy traces are kept.
 */
function shouldKeepHealthyTransaction(traceId: string): boolean {
  let hash = 0;
  for (let index = 0; index < traceId.length; index += 1) {
    // biome-ignore lint/suspicious/noBitwiseOperators: intentional 32-bit overflow to preserve stable sampling decisions
    hash = (Math.imul(hash, 31) + traceId.charCodeAt(index)) | 0;
  }
  // biome-ignore lint/suspicious/noBitwiseOperators: intentional unsigned normalization before applying sample threshold
  return (hash >>> 0) / MAX_UINT_32 < HEALTHY_TRANSACTION_SAMPLE_RATE;
}

export function filterTransactionsWithHealthySampling(event: TransactionEvent): TransactionEvent | null {
  const rootStatus = event.contexts?.trace?.status;
  const hasErroredChildSpan = (event.spans ?? []).some((span) => hasErrorStatus(span.status));

  if (hasErrorStatus(rootStatus) || hasErroredChildSpan) {
    return event;
  }

  const traceId = event.contexts?.trace?.trace_id ?? event.event_id;
  if (!traceId) {
    return null;
  }

  return shouldKeepHealthyTransaction(traceId) ? event : null;
}
